import React from 'react';
import styled, { css } from '../../styles/styled-components';
import { ellipsis } from 'polished';

export type TableProps<T> = {
  rowSelectable?: boolean;
  divided?: boolean;
  rowHoverHighlight?: boolean;
  maxWidth?: boolean;
};

export class Table<T extends {}> extends React.Component<TableProps<T>, {}> {
  render() {
    const { children, divided, ...rest } = this.props;

    return (
      <StyledTable divided={divided} {...rest}>
        {children}
      </StyledTable>
    );
  }
}

type StyledTableProps = {
  divided?: boolean;
  maxWidth?: boolean;
} & JSX.IntrinsicElements['table'];

const StyledTable = styled<StyledTableProps>(
  ({ divided, maxWidth, ...rest }) => <table {...rest} />,
)`
  border-collapse: collapse;

  ${p =>
    p.divided
      ? css`
          ${TableRow} {
            border-bottom: 1px solid ${p => p.theme.dividerColor};
          }

          ${TableRow}:last-child {
            border-bottom: 0;
          }
        `
      : ''};
  ${p =>
    p.maxWidth
      ? css`
          width: 100%;
        `
      : ``};
`;

export const TableHead = styled.thead``;

export const TableBody = styled.tbody``;

type TableRowProps = {
  clickable?: boolean;
  rowHoverHighlight?: boolean;
} & JSX.IntrinsicElements['tr'];

export const TableRow = styled<TableRowProps>(
  ({ clickable, rowHoverHighlight, ...rest }) => <tr {...rest} />,
)`
  ${p =>
    p.clickable
      ? css`
          cursor: pointer;
        `
      : ''};
  ${p =>
    p.rowHoverHighlight
      ? css`
          &:hover {
            background-color: ${p => p.theme.grayBackground} !important;
          }
        `
      : ''};
`;

export type Align = 'left' | 'center' | 'right';

export const tableHeadCellStyle = (al?: Align) => css`
  padding: 10px 10px;
  font-weight: 700;
  ${align(al)};
`;

export type TableHeadCellProps = { align?: Align; width?: string };

export const TableHeadCell = styled<TableHeadCellProps>(
  ({ align, ...rest }) => <th {...rest} />,
)`
  width: ${p => p.width || 'auto'};
  ${p => tableHeadCellStyle(p.align)};
`;

const align = (align?: Align) => {
  switch (align) {
    case 'center':
      return css`
        text-align: center;
      `;
    case 'right':
      return css`
        text-align: right;
      `;
    case 'left':
    default:
      return css`
        text-align: left;
      `;
  }
};

export type TableCellProps = {
  align?: Align;
  showOnRowHover?: boolean;
  ellipsis?: string | number;
  title?: string | ((item: any) => string);
};

export const TableCell = styled<TableCellProps>(
  ({ align, showOnRowHover, ellipsis, title, ...rest }) => (
    <td title={typeof title == 'string' && ellipsis ? title : ''} {...rest} />
  ),
)`
  padding: 10px 10px;

  ${p => align(p.align)};
  ${p => (p.ellipsis ? ellipsis(p.ellipsis).toString() : '')};
  // To override display from polished ellipsis
  display: table-cell;

  ${p =>
    p.showOnRowHover
      ? css`
          opacity: 0;

          ${TableRow}:hover & {
            opacity: 1;
          }
        `
      : ''};
`;
